'use client';

import * as SwitchPrimitive from '@radix-ui/react-switch';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '@workspace/ui/lib';
import type * as React from 'react';

function Toggle({ className, ...props }: React.ComponentProps<typeof SwitchPrimitive.Root>) {
  return (
    <SwitchPrimitive.Root
      data-slot="switch"
      className={cn(
        'peer data-[state=unchecked]:bg-grey-400 cursor-pointer dark:data-[state=unchecked]:bg-grey-400 focus-visible:border-ring focus-visible:ring-ring/50 flex h-6 w-10 shrink-0 items-center rounded-full border border-transparent transition-all outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-pink-400 dark:data-[state=checked]:bg-pink-400',
        className
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        data-slot="switch-thumb"
        className={cn(
          'pointer-events-none block size-5 rounded-full bg-neutral-100 ring-0 transition-transform data-[state=checked]:translate-x-[calc(100%-4px)] data-[state=unchecked]:translate-x-[calc(0%+2px)] dark:data-[state=checked]:bg-neutral-100 dark:data-[state=unchecked]:bg-neutral-100'
        )}
      />
    </SwitchPrimitive.Root>
  );
}

function ToggleTabs({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn('flex flex-col gap-2', className)}
      {...props}
    />
  );
}

function ToggleTabsList({
  className,
  hideInactiveLabels,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> & {
  hideInactiveLabels?: boolean;
}) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(
        'bg-grey-25 dark:bg-grey-200 text-grey-700 border-grey-1100 dark:border-grey-600 flex h-9 w-fit items-center justify-center rounded-2xl border p-[1px]',
        hideInactiveLabels ? 'md:min-w-[176px]' : 'md:min-w-[400px]',
        className
      )}
      {...props}
    />
  );
}

function ToggleTabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-2xl border border-transparent px-4 py-0.5 text-base whitespace-nowrap transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-neutral-200 data-[state=active]:font-medium data-[state=active]:text-neutral-100 data-[state=active]:shadow-sm data-[state=inactive]:font-normal dark:data-[state=active]:bg-neutral-100 dark:data-[state=active]:text-neutral-200 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 dark:text-grey-700",
        className
      )}
      {...props}
    />
  );
}

export { Toggle, ToggleTabs, ToggleTabsList, ToggleTabsTrigger };
